import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authFetch } from "../../utils/authFetch/authFetch";
import AdminPanel from "./components/AdminPanel";
import {
  UsersIcon,
  FolderIcon,
  CalendarIcon,
  Cog6ToothIcon,
  ArrowLeftIcon,
  HomeIcon,
  BoltIcon,
  BellIcon,
  AtSymbolIcon,
  CheckCircleIcon,
  PlusIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CommunityDetailPage = ({ collapsed, setCollapsed, user }) => {
  // communityId can be either a slug or an ID - backend handles both
  const { communityId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const username = localStorage.getItem("username");

  const [community, setCommunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  // Center tabs (Home, Feed, Members)
  const [activeTab, setActiveTab] = useState("feed");
  // Left nav selection (Home, Project Hub, Events). Tasks intentionally excluded per request.
  const [activeLeftNav, setActiveLeftNav] = useState("home");
  // Subscribe toggle mirrors membership; initialize false and update when community loads
  const [subscribed, setSubscribed] = useState(false);
  // Project Hub state
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  // Explicit membership check state
  const [membership, setMembership] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  // Events state
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  // Admin panel state
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  useEffect(() => {
    const role = (membership || community?.userMembership)?.role;
    const status = (membership || community?.userMembership)?.status;
    const isMemberNow = status === "active" || role === "admin";
    setSubscribed(!!isMemberNow);
  }, [community, membership]);

  useEffect(() => {
    fetchCommunity();
    fetchMembership(); // Also fetch membership on initial load
  }, [communityId]);

  // Fetch projects when Project Hub is opened or communityId changes
  useEffect(() => {
    if (activeLeftNav === "projects") {
      // Check membership from backend then load projects
      fetchMembership().finally(() => fetchProjects());
    } else if (activeLeftNav === "events") {
      // Fetch events when Events is opened
      fetchMembership().finally(() => fetchEvents());
    }
  }, [activeLeftNav, communityId]);

  const fetchCommunity = async () => {
    setLoading(true);
    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}`,
        {},
        navigate
      );

      const data = await res.json();
      if (res.ok) {
        setCommunity(data);
      } else {
        console.error("Failed to fetch community:", data.message);
      }
    } catch (error) {
      console.error("Error fetching community:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembership = async () => {
    setMembershipLoading(true);
    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/membership/me`,
        {},
        navigate
      );
      const data = await res.json();
      if (res.ok) {
        setMembership(data);
      } else {
        setMembership(null);
      }
    } catch (e) {
      console.error("Error checking membership:", e);
    } finally {
      setMembershipLoading(false);
    }
  };

  const handleCreateProject = () => {
    if (username) {
      navigate(`/${username}/create?communityId=${communityId}`);
    } else {
      navigate(`/login`);
    }
  };

  const fetchProjects = async () => {
    setProjectsLoading(true);
    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/projects`,
        {},
        navigate
      );
      const data = await res.json();
      if (res.ok) {
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setProjectsLoading(false);
    }
  };

  const fetchEvents = async () => {
    setEventsLoading(true);
    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/events?upcoming=true`,
        {},
        navigate
      );
      const data = await res.json();
      if (res.ok) {
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setEventsLoading(false);
    }
  };

  const handleCreateEvent = async (eventData) => {
    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/events`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(eventData),
        },
        navigate
      );

      const data = await res.json();
      if (res.ok) {
        setShowCreateEventModal(false);
        fetchEvents(); // Refresh events
      } else {
        alert(data.message || "Failed to create event");
      }
    } catch (error) {
      console.error("Error creating event:", error);
      alert("An error occurred. Please try again.");
    }
  };

  const handleAttendEvent = async (eventId) => {
    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/events/${eventId}/attend`,
        {
          method: "POST",
        },
        navigate
      );

      const data = await res.json();
      if (res.ok) {
        fetchEvents(); // Refresh events
      } else {
        alert(data.message || "Failed to attend event");
      }
    } catch (error) {
      console.error("Error attending event:", error);
      alert("An error occurred. Please try again.");
    }
  };

  const handleUnattendEvent = async (eventId) => {
    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/events/${eventId}/unattend`,
        {
          method: "POST",
        },
        navigate
      );

      const data = await res.json();
      if (res.ok) {
        fetchEvents(); // Refresh events
      } else {
        alert(data.message || "Failed to unattend event");
      }
    } catch (error) {
      console.error("Error unattending event:", error);
      alert("An error occurred. Please try again.");
    }
  };

  const handleJoinCommunity = async () => {
    setJoining(true);
    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/join`,
        {
          method: "POST",
        },
        navigate
      );

      const data = await res.json();
      if (res.ok) {
        // Refresh community data to update membership status
        fetchCommunity();
      } else {
        alert(data.message || "Failed to join community");
      }
    } catch (error) {
      console.error("Error joining community:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setJoining(false);
    }
  };

  const handleLeaveCommunity = async () => {
    if (!window.confirm("Are you sure you want to leave this community?")) {
      return;
    }

    try {
      const res = await authFetch(
        `${BACKEND_URL}/communities/${communityId}/leave`,
        {
          method: "POST",
        },
        navigate
      );

      const data = await res.json();
      if (res.ok) {
        // Refresh community data
        fetchCommunity();
      } else {
        alert(data.message || "Failed to leave community");
      }
    } catch (error) {
      console.error("Error leaving community:", error);
      alert("An error occurred. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading community...</p>
        </div>
      </div>
    );
  }

  if (!community) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Community not found</h2>
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:text-blue-700"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  const membershipData = membership || community.userMembership || {};
  const isAdmin = membershipData.role === "admin";
  const isMember = membershipData.isMember || membershipData.status === "active" || isAdmin;
  const isPending = membershipData.status === "pending";

  // Debug logging
  console.log("🔍 Membership Debug:", {
    membership,
    communityUserMembership: community.userMembership,
    membershipData,
    isAdmin,
    isMember,
    role: membershipData.role,
    status: membershipData.status
  });

  // Filler announcements for Feed tab
  const announcements = [
    {
      id: 1,
      icon: <BellIcon className="w-4 h-4" />, // alert
      title: "Super Admin mentioned the community",
      meta: "Alert • 1 day ago",
    },
    {
      id: 2,
      icon: <AtSymbolIcon className="w-4 h-4" />, // reply
      title: "@Alex H replied to your comment",
      meta: "Alert • 1 day ago",
    },
    {
      id: 3,
      icon: <span className="text-gray-700 text-lg leading-none">＋</span>, // join symbol
      title: "You joined the community",
      meta: "Alert • 1 day ago",
    },
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Cover/Banner */}
      <div
        className="h-40 md:h-48 bg-gradient-to-r from-blue-400 to-purple-500 relative"
        style={
          community.coverImage
            ? {
                backgroundImage: `url(${community.coverImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : {}
        }
      >
        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white transition flex items-center gap-2 text-gray-800 text-sm font-medium"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back
        </button>

        {/* Admin Buttons */}
        {isAdmin && (
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <button
              onClick={() => setShowAdminPanel(true)}
              className="px-3 py-1.5 bg-blue-600 backdrop-blur-sm rounded-lg hover:bg-blue-700 transition flex items-center gap-2 text-white text-sm font-medium"
            >
              <ShieldCheckIcon className="w-4 h-4" />
              Admin Panel
            </button>
            <button
              onClick={() => navigate(`/communities/${communityId}/settings`)}
              className="px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white transition flex items-center gap-2 text-gray-800 text-sm font-medium"
            >
              <Cog6ToothIcon className="w-4 h-4" />
              Settings
            </button>
          </div>
        )}
      </div>

      {/* Main content container */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 -mt-10 relative z-10">
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
          {/* Breadcrumb + search */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="text-sm text-gray-500">
                Communities / <span className="text-gray-800 font-medium">{community.name}</span>
              </div>
              {isAdmin && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                  <ShieldCheckIcon className="w-3.5 h-3.5" />
                  Administrator
                </span>
              )}
            </div>
            <div className="hidden md:flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-1.5 text-sm text-gray-500 w-64">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 105.64 5.64a7.5 7.5 0 0010.61 10.61z" />
              </svg>
              <input className="bg-transparent outline-none flex-1 placeholder:text-gray-400" placeholder="Search" />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left sidebar */}
            <aside className="lg:col-span-3 space-y-4">
              {/* Left nav (no Tasks) */}
              <nav className="space-y-2">
                <LeftNavItem
                  icon={<HomeIcon className="w-4 h-4" />}
                  label="Home"
                  active={activeLeftNav === "home"}
                  onClick={() => setActiveLeftNav("home")}
                />
                <LeftNavItem
                  icon={<FolderIcon className="w-4 h-4" />}
                  label="Project Hub"
                  active={activeLeftNav === "projects"}
                  onClick={() => setActiveLeftNav("projects")}
                />
                <LeftNavItem
                  icon={<CalendarIcon className="w-4 h-4" />}
                  label="Events"
                  active={activeLeftNav === "events"}
                  onClick={() => setActiveLeftNav("events")}
                />
              </nav>
            </aside>

            {/* Center content */}
            <main className="lg:col-span-6">
              {activeLeftNav === "projects" ? (
                <ProjectHub
                  loading={projectsLoading}
                  membershipLoading={membershipLoading}
                  projects={projects}
                  onOpenProject={(id) => navigate(`/projects/${id}/details`)}
                  isMember={isMember}
                  onCreateProject={handleCreateProject}
                  onJoin={handleJoinCommunity}
                />
              ) : activeLeftNav === "events" ? (
                <EventsHub
                  loading={eventsLoading}
                  membershipLoading={membershipLoading}
                  events={events}
                  isMember={isMember}
                  isAdmin={isAdmin}
                  onCreateEvent={() => setShowCreateEventModal(true)}
                  onAttendEvent={handleAttendEvent}
                  onUnattendEvent={handleUnattendEvent}
                  onJoin={handleJoinCommunity}
                />
              ) : (
                <>
                  {/* Tabs only visible in Home mode */}
                  <div className="flex gap-3 mb-3">
                    {[
                      { id: "home", label: "Home" },
                      { id: "feed", label: "Feed" },
                      { id: "members", label: "Members" },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-3 py-1.5 text-sm rounded-full transition ${
                          activeTab === tab.id
                            ? "bg-gray-900 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Tab content */}
                  <div className="space-y-3">
                    {activeTab === "home" && (
                      <div className="rounded-xl border border-gray-200 p-4">
                        <h3 className="font-semibold text-gray-800 mb-1">Welcome</h3>
                        <p className="text-sm text-gray-600">
                          This is the home view for <span className="font-medium">{community.name}</span>. Explore the latest updates, projects, and events in this community.
                        </p>
                      </div>
                    )}

                    {activeTab === "feed" && (
                      <div className="space-y-3">
                        {announcements.map((a) => (
                          <div key={a.id} className="rounded-xl border border-gray-200 p-3 flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center">
                              {a.icon}
                            </div>
                            <div className="flex-1">
                              <div className="text-sm text-gray-800">{a.title}</div>
                            </div>
                            <div className="text-xs text-gray-500">{a.meta}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {activeTab === "members" && (
                      <div className="rounded-xl border border-gray-200 p-4">
                        <div className="text-sm text-gray-600">Members list will appear here.</div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </main>

            {/* Right sidebar */}
            <aside className="lg:col-span-3 space-y-4">
              <div className="rounded-xl border border-gray-200 p-4">
                <h2 className="text-sm font-semibold text-gray-800 mb-2">Name of Community</h2>
                <p className="text-sm text-gray-600 mb-3">
                  {community.description || "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book."}
                </p>

                <div className="flex items-center gap-2 text-xs text-gray-600 mb-3">
                  <CheckCircleIcon className="w-4 h-4 text-gray-700" />
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">Created in {new Date().toLocaleString('en-US', { month: 'short', year: 'numeric' })}</span>
                </div>

                {/* Members count */}
                <div className="rounded-lg border border-gray-200 p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-gray-800">
                    <UsersIcon className="w-4 h-4" />
                    Members
                  </div>
                  <div className="text-sm text-gray-600">{community.memberCount || 50}</div>
                </div>

                {/* Subscribe */}
                <button
                  onClick={async () => {
                    try {
                      if (subscribed) {
                        await handleLeaveCommunity();
                        setSubscribed(false);
                      } else {
                        await handleJoinCommunity();
                        setSubscribed(true);
                      }
                    } catch {}
                  }}
                  className={`mt-3 w-full text-sm px-3 py-2 rounded-lg border transition ${
                    subscribed
                      ? "bg-gray-100 text-gray-700 border-gray-200"
                      : "bg-white text-gray-900 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {subscribed ? "Subscribed" : "Subscribe"}
                </button>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* Create Event Modal */}
      {showCreateEventModal && (
        <CreateEventModal
          onClose={() => setShowCreateEventModal(false)}
          onSubmit={handleCreateEvent}
        />
      )}

      {/* Admin Panel */}
      {showAdminPanel && isAdmin && (
        <AdminPanel
          communityId={communityId}
          community={community}
          onClose={() => setShowAdminPanel(false)}
          onUpdate={fetchCommunity}
        />
      )}
    </div>
  );
};

// Small left nav item component (local)
const LeftNavItem = ({ icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${
      active ? "bg-gray-900 text-white" : "text-gray-700 hover:bg-gray-100"
    }`}
  >
    <span className="inline-flex items-center justify-center w-5 h-5">{icon}</span>
    <span>{label}</span>
  </button>
);

export default CommunityDetailPage;

// Events Hub: shows community events
const EventsHub = ({ loading, membershipLoading, events, isMember, isAdmin, onCreateEvent, onAttendEvent, onUnattendEvent, onJoin }) => {
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">📅</div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">No upcoming events</h3>
        <p className="text-gray-500">
          {isMember ? "Be the first to create an event for this community!" : "Join the community to view and create events."}
        </p>
        {membershipLoading ? (
          <button disabled className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-200 text-gray-500 cursor-not-allowed">
            Checking membership…
          </button>
        ) : isMember ? (
          <button onClick={onCreateEvent} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black">
            <PlusIcon className="w-5 h-5" /> Create event
          </button>
        ) : (
          <button onClick={onJoin} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
            Join community
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        {membershipLoading ? (
          <button disabled className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-200 text-gray-500 text-sm cursor-not-allowed">
            Checking membership…
          </button>
        ) : isMember ? (
          <button onClick={onCreateEvent} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-900 text-white hover:bg-black text-sm">
            <PlusIcon className="w-4 h-4" /> Create event
          </button>
        ) : (
          <button onClick={onJoin} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm">
            Join community
          </button>
        )}
      </div>
      <div className="space-y-3">
        {events.map((event) => (
          <EventCard
            key={event.id}
            event={event}
            isMember={isMember}
            onAttend={() => onAttendEvent(event.id)}
            onUnattend={() => onUnattendEvent(event.id)}
          />
        ))}
      </div>
    </div>
  );
};

// Event Card Component
const EventCard = ({ event, isMember, onAttend, onUnattend }) => {
  const parseDate = (dateValue) => {
    if (!dateValue) return null;
    // Handle Firestore Timestamp
    if (dateValue._seconds) {
      return new Date(dateValue._seconds * 1000);
    }
    // Handle Firestore Timestamp with toDate method
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }
    // Handle ISO string or timestamp
    const parsed = new Date(dateValue);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const startDate = parseDate(event.startDate);
  const endDate = parseDate(event.endDate);

  const formatDate = (date) => {
    if (!date) return "Date TBD";
    try {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(date);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid date";
    }
  };

  const getLocationDisplay = () => {
    if (!event.location) return "Location TBD";
    if (event.location.type === "online") return "🌐 Online";
    if (event.location.type === "physical") return `📍 ${event.location.address || "Physical location"}`;
    if (event.location.type === "hybrid") return "🔄 Hybrid (Online + Physical)";
    return "Location TBD";
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-800 mb-1">{event.title}</h3>
          <p className="text-sm text-gray-600 line-clamp-2 mb-2">{event.description}</p>
        </div>
        {event.status && (
          <span className={`ml-3 px-2 py-1 text-xs rounded-full whitespace-nowrap ${
            event.status === "ongoing" ? "bg-green-100 text-green-700" :
            event.status === "upcoming" ? "bg-blue-100 text-blue-700" :
            event.status === "completed" ? "bg-gray-100 text-gray-700" :
            "bg-red-100 text-red-700"
          }`}>
            {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
          </span>
        )}
      </div>

      {/* Event details */}
      <div className="space-y-1 mb-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <CalendarIcon className="w-4 h-4" />
          <span>{startDate ? formatDate(startDate) : "Date TBD"}</span>
          {endDate && <span>→ {formatDate(endDate)}</span>}
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>{getLocationDisplay()}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <UsersIcon className="w-4 h-4" />
          <span>{event.attendeeCount || 0} attending</span>
          {event.maxAttendees && <span>(Max: {event.maxAttendees})</span>}
        </div>
      </div>

      {/* Tags */}
      {event.tags && event.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {event.tags.slice(0, 3).map((tag, i) => (
            <span key={i} className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Attend button */}
      {isMember && event.status !== "completed" && event.status !== "cancelled" && (
        <div className="flex gap-2">
          {event.isUserAttending ? (
            <button
              onClick={onUnattend}
              className="flex-1 px-3 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
            >
              Unattend
            </button>
          ) : (
            <button
              onClick={onAttend}
              disabled={event.maxAttendees && event.attendeeCount >= event.maxAttendees}
              className={`flex-1 px-3 py-2 text-sm rounded-lg transition ${
                event.maxAttendees && event.attendeeCount >= event.maxAttendees
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              {event.maxAttendees && event.attendeeCount >= event.maxAttendees ? "Event Full" : "Attend"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Create Event Modal Component
const CreateEventModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    locationType: "online",
    locationAddress: "",
    locationUrl: "",
    maxAttendees: "",
    tags: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    const eventData = {
      title: formData.title,
      description: formData.description,
      startDate: new Date(formData.startDate).toISOString(),
      endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
      location: {
        type: formData.locationType,
        address: formData.locationType === "physical" || formData.locationType === "hybrid" ? formData.locationAddress : undefined,
        url: formData.locationType === "online" || formData.locationType === "hybrid" ? formData.locationUrl : undefined,
      },
      maxAttendees: formData.maxAttendees ? parseInt(formData.maxAttendees) : null,
      tags: formData.tags ? formData.tags.split(",").map(t => t.trim()).filter(t => t) : [],
    };

    onSubmit(eventData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-800">Create Event</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter event title"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Describe your event"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date & Time *
                </label>
                <input
                  type="datetime-local"
                  required
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Location Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location Type
              </label>
              <select
                value={formData.locationType}
                onChange={(e) => setFormData({ ...formData, locationType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="online">Online</option>
                <option value="physical">Physical</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>

            {/* Location Details */}
            {(formData.locationType === "physical" || formData.locationType === "hybrid") && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Physical Address
                </label>
                <input
                  type="text"
                  value={formData.locationAddress}
                  onChange={(e) => setFormData({ ...formData, locationAddress: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter address"
                />
              </div>
            )}

            {(formData.locationType === "online" || formData.locationType === "hybrid") && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meeting URL
                </label>
                <input
                  type="url"
                  value={formData.locationUrl}
                  onChange={(e) => setFormData({ ...formData, locationUrl: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://..."
                />
              </div>
            )}

            {/* Max Attendees */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Attendees (Optional)
              </label>
              <input
                type="number"
                min="1"
                value={formData.maxAttendees}
                onChange={(e) => setFormData({ ...formData, maxAttendees: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Leave empty for unlimited"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="workshop, networking, beginner-friendly"
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-black transition"
              >
                Create Event
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Project Hub: shows community-linked projects
const ProjectHub = ({ loading, membershipLoading, projects, onOpenProject, isMember, onCreateProject, onJoin }) => {
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">📁</div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">No projects yet</h3>
        <p className="text-gray-500">
          {isMember ? "Be the first to add a project to this community!" : "Join the community to view projects."}
        </p>
        {membershipLoading ? (
          <button disabled className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-200 text-gray-500 cursor-not-allowed">
            Checking membership…
          </button>
        ) : isMember ? (
          <button onClick={onCreateProject} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black">
            <PlusIcon className="w-5 h-5" /> Create project
          </button>
        ) : (
          <button onClick={onJoin} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
            Join community
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        {membershipLoading ? (
          <button disabled className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-200 text-gray-500 text-sm cursor-not-allowed">Checking membership…</button>
        ) : isMember ? (
          <button onClick={onCreateProject} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-900 text-white hover:bg-black text-sm">
            <PlusIcon className="w-4 h-4" /> Create project
          </button>
        ) : (
          <button onClick={onJoin} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm">
            Join community
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {projects.map((item) => (
        <button
          key={item.linkId || item.id}
          onClick={() => onOpenProject(item.project?.id || item.id)}
          className="bg-white rounded-xl border border-gray-200 hover:shadow-sm transition text-left p-4"
        >
          {item.isPinned && (
            <span className="inline-block mb-2 px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">📌 Pinned</span>
          )}
          {item.project?.image && (
            <img src={item.project.image} alt={item.project.title} className="w-full h-40 object-cover rounded-lg mb-3" />
          )}
          <h3 className="font-semibold text-gray-800 line-clamp-1 mb-1">{item.project?.title || item.title}</h3>
          <p className="text-sm text-gray-600 line-clamp-2">{item.project?.description || item.description}</p>
          {!!(item.project?.tags?.length) && (
            <div className="mt-2 flex flex-wrap gap-2">
              {item.project.tags.slice(0, 3).map((tag, i) => (
                <span key={i} className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">{tag}</span>
              ))}
            </div>
          )}
        </button>
      ))}
      </div>
    </div>
  );
};
